import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Marketing from './pages/Marketing';
import Administracao from './pages/Administracao';
import Relatorios from './pages/Relatorios';
import GerarCobranca from './pages/GerarCobranca';
import Pagamentos from './pages/Pagamentos';
import Atendimento from './pages/Atendimento';
import Envio from './pages/Envio';
import AgendamentoMedico from './pages/agendamentos/AgendamentoMedico';
import AgendamentoOdontologia from './pages/agendamentos/AgendamentoOdontologia';
import AgendamentoCarteiraOdonto from './pages/agendamentos/AgendamentoCarteiraOdonto';
import AgendamentosConsultorio from './pages/AgendamentosConsultorio';
import ConferenciaValoresExames from './pages/ConferenciaValoresExames';
import EsteiraVendas from './pages/EsteiraVendas';
import { setOnlineStatus } from './services/api';
import { processSyncQueue } from './lib/sync';
import CustomerService from './pages/CustomerService';
import MarketingReports from './pages/MarketingReports';
import MarketingContacts from './pages/MarketingContacts';
import Cobrancas from './pages/Cobrancas';
import CobrancasTodas from './pages/CobrancasTodas';
import CobrancasAvulsas from './pages/CobrancasAvulsas';
import CobrancasParcelamentos from './pages/CobrancasParcelamentos';
import CobrancasAssinaturas from './pages/CobrancasAssinaturas';
import CobrancasCpfl from './pages/CobrancasCpfl';
import PagantesMes from './pages/PagantesMes';
import CobrancasPix from './pages/CobrancasPix';
import Checklist from './pages/Checklist';
import ConsultaClientes from './pages/ConsultaClientes';
import MeusClientes from './pages/MeusClientes';
import SplitPayment from './pages/SplitPayment';
import ConsultaUC from './pages/ConsultaUC';
import DataBase from './pages/DataBase';
import CadastroClientes from './pages/CadastroClientes';
import ClicksignEnvios from './pages/ClicksignEnvios';
import ClicksignAcompanhar from './pages/ClicksignAcompanhar';
import SuporteHome from './pages/SuporteHome';
import MeuPerfil from './pages/MeuPerfil';

// ═══════════════════════════════════════════════════════════════════════════════
// 🔧 FUNÇÃO AUXILIAR - Normaliza IDs (aceita hífen e underscore)
// ═══════════════════════════════════════════════════════════════════════════════
const normalizeId = (id) => id?.toLowerCase().replace(/_/g, '-') || '';

// ═══════════════════════════════════════════════════════════════════════════════
// 🔐 COMPONENTE DE ROTA PROTEGIDA (CORRIGIDO)
// ═══════════════════════════════════════════════════════════════════════════════
function ProtectedRoute({ children, requiredPermission }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // ✅ DEBUG: Log para ver o que está acontecendo
  if (process.env.NODE_ENV === 'development') {
    console.log('🔐 [ProtectedRoute] Rota:', location.pathname);
    console.log('🔐 [ProtectedRoute] Usuário:', user?.name || user?.email);
    console.log('🔐 [ProtectedRoute] Permissões:', user?.permissions);
    console.log('🔐 [ProtectedRoute] Roles:', user?.roles);
    console.log('🔐 [ProtectedRoute] Permissão requerida:', requiredPermission);
  }

  // Se nenhuma permissão específica for exigida, libera
  if (!requiredPermission) return children;

  // ✅ Normaliza a permissão requerida (aceita hífen e underscore)
  const requeridas = (Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission]).map(normalizeId);
  const normalizedRequired = requeridas.join(' ou ');
  
  // ✅ Verifica se o usuário tem a permissão (normalizada)
  const userPermissions = user?.permissions || [];
  const hasPermission = userPermissions.some(perm => requeridas.includes(normalizeId(perm)));

  // Admin e Gestor têm acesso à área de conferência de repasses.
  const isAdmin = (user?.roles || []).some((role) => String(role).toLowerCase() === 'admin');
  const isGestor = (user?.roles || []).some((role) =>
    String(role).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() === 'gestor'
  );
  const isRepasseParceiro = requeridas.includes('repasse-parceiro');
const isSuporte = requeridas.some((permissao) =>
  permissao === 'suporte' || permissao.startsWith('suporte:')
);
const acessoPadraoSuporte = isSuporte && userPermissions.length === 0 && (isAdmin || isGestor);

  const isDataBaseRoute = requeridas.includes('data-base');

  const autorizado = isDataBaseRoute
    ? hasPermission
    : isSuporte
      ? (hasPermission || acessoPadraoSuporte)
      : (isAdmin || (isGestor && isRepasseParceiro) || hasPermission);
  if (autorizado) {
    return children;
  }

  // ✅ DEBUG: Log de acesso negado
  if (process.env.NODE_ENV === 'development') {
    console.warn('⛔ [ProtectedRoute] Acesso negado!');
    console.warn('⛔ [ProtectedRoute] Permissão faltando:', normalizedRequired);
    console.warn('⛔ [ProtectedRoute] Permissões do usuário:', userPermissions);
  }

  // Sem permissão: redireciona para o Dashboard
  return <Navigate to="/" replace />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// 📋 MAPEAMENTO DE ROTAS PARA PERMISSÕES (NORMALIZADO)
// ═══════════════════════════════════════════════════════════════════════════════
const routePermissionMap = {
  '/':                       'dashboard',
  '/consulta-uc':           'consulta-uc',
  '/clients':                'clientes',
  '/meus-clientes/consulta': 'clientes',
  '/meus-clientes/cadastro': 'clientes',
  '/repasse-parceiro/split-payment': 'repasse-parceiro',
  '/pagamentos':             'pagamentos',
  '/gerar-cobranca':         'gerar-cobranca',
  '/relatorios':             'relatorios',
  '/marketing':              'marketing',
  '/envio':                  'envio',
  '/esteira-vendas':         'esteira-vendas',
  '/agendamentos':           'agendamentos',
  '/atendimento':            'atendimento',
  '/administracao':          'administracao',
  '/cobrancas':              'cobrancas',
  '/cobrancas/todas':        'cobrancas',
  '/cobrancas/avulsas':      'cobrancas',
  '/cobrancas/parcelamentos':'cobrancas',
  '/cobrancas/assinaturas':  'cobrancas',
  '/cobrancas/cpfl':         'cobrancas',
  '/cobrancas/pix':          'cobrancas',
  '/relatorios-marketing':   'marketing',
  '/marketing-contacts':     'marketing',
  '/clicksign/contrato-adesao': 'clicksign',
  '/clicksign/termo-cancelamento': 'clicksign',
  '/clicksign/acompanhar': 'clicksign',
'/suporte/asaas': 'suporte',
'/suporte/cpfl': 'suporte',
'/suporte/pix-tools': 'suporte',

};

// ═══════════════════════════════════════════════════════════════════════════════
// 🧩 WRAPPER DE CLIENTES
// ═══════════════════════════════════════════════════════════════════════════════
function ClientsWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialClient = location.state?.client || null;
  const openEdit = Boolean(location.state?.openEdit);

  return (
    <Clients
      initialClient={initialClient}
      openEdit={openEdit}
      onNavigate={(page, state) => {
        console.log('🟢 onNavigate chamado, page:', page, 'state:', state);
        sessionStorage.setItem('gc_cliente', JSON.stringify(state?.client));
        navigate(`/${page}`, { state });
      }}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 🎨 COMPONENTE PRINCIPAL (APP)
// ═══════════════════════════════════════════════════════════════════════════════
function App() {
  useEffect(() => {
    const handleOnline = () => {
      setOnlineStatus(true);
      processSyncQueue();
    };
    const handleOffline = () => setOnlineStatus(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    setOnlineStatus(navigator.onLine);
    if (navigator.onLine) processSyncQueue();
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />

        {/* Rotas protegidas pelo Layout */}
        <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<Dashboard />} />
          <Route
            path="consulta-uc"
            element={
              <ProtectedRoute requiredPermission="consulta-uc">
                <ConsultaUC />
              </ProtectedRoute>
            }
          />
          <Route
            path="data-base"
            element={
              <ProtectedRoute requiredPermission="data-base">
                <DataBase />
              </ProtectedRoute>
            }
          />
          <Route
            path="data-base/:baseId"
            element={
              <ProtectedRoute requiredPermission="data-base">
                <DataBase />
              </ProtectedRoute>
            }
          />
          
          {/* Meus Clientes */}
          <Route path="meus-clientes">
            <Route
              index
              element={
                <ProtectedRoute requiredPermission="clientes">
                  <MeusClientes />
                </ProtectedRoute>
              }
            />
            <Route
              path="consulta"
              element={
                <ProtectedRoute requiredPermission={['clientes', 'clientes:consulta']}>
                  <ConsultaClientes />
                </ProtectedRoute>
              }
            />
            <Route
              path="cadastro"
              element={
                <ProtectedRoute requiredPermission={['clientes', 'clientes:cadastro']}>
                  <CadastroClientes />
                </ProtectedRoute>
              }
            />
          </Route>
          
          {/* Repasse Parceiro — consulta para conferência do gestor */}
          <Route
            path="repasse-parceiro/split-payment"
            element={
              <ProtectedRoute requiredPermission="repasse-parceiro">
                <SplitPayment />
              </ProtectedRoute>
            }
          />

          <Route path="suporte/asaas" element={
            <ProtectedRoute requiredPermission={['suporte', 'suporte:asaas']}>
              <SuporteHome sistema="ASAAS" />
            </ProtectedRoute>
          } />
          <Route path="suporte/cpfl" element={
            <ProtectedRoute requiredPermission={['suporte', 'suporte:cpfl']}>
              <SuporteHome sistema="CPFL" />
            </ProtectedRoute>
          } />
          <Route path="suporte/pix-tools" element={
            <ProtectedRoute requiredPermission={['suporte', 'suporte:pix-tools']}>
              <SuporteHome sistema="PIX TOOLS" />
            </ProtectedRoute>
          } />

          {/* Clientes (rota antiga - compatibilidade) */}
          <Route
            path="clients"
            element={
              <ProtectedRoute requiredPermission="clientes">
                <ClientsWrapper />
              </ProtectedRoute>
            }
          />
          
          {/* Pagamentos */}
          <Route
            path="pagamentos"
            element={
              <ProtectedRoute requiredPermission="pagamentos">
                <Pagamentos />
              </ProtectedRoute>
            }
          />
          
          {/* Cobranças */}
          <Route path="cobrancas">
            <Route index element={<Navigate to="/cobrancas/todas" replace />} />
            <Route
              path="todas"
              element={
                <ProtectedRoute requiredPermission="cobrancas">
                  <CobrancasTodas />
                </ProtectedRoute>
              }
            />
            <Route
              path="avulsas"
              element={
                <ProtectedRoute requiredPermission="cobrancas">
                  <CobrancasAvulsas />
                </ProtectedRoute>
              }
            />
            <Route
              path="parcelamentos"
              element={
                <ProtectedRoute requiredPermission="cobrancas">
                  <CobrancasParcelamentos />
                </ProtectedRoute>
              }
            />
            <Route
              path="assinaturas"
              element={
                <ProtectedRoute requiredPermission="cobrancas">
                  <CobrancasAssinaturas />
                </ProtectedRoute>
              }
            />
            <Route
              path="cpfl"
              element={
                <ProtectedRoute requiredPermission="cobrancas">
                  <CobrancasCpfl />
                </ProtectedRoute>
              }
            />
            <Route
              path="pagantes-mes"
              element={
                <ProtectedRoute requiredPermission="cobrancas">
                  <PagantesMes />
                </ProtectedRoute>
              }
            />
            <Route
              path="pix"
              element={
                <ProtectedRoute requiredPermission="cobrancas">
                  <CobrancasPix />
                </ProtectedRoute>
              }
            />
          </Route>
          
          {/* ✅ Gerar Cobrança - PERMISSÃO CORRIGIDA */}
          <Route
            path="gerar-cobranca"
            element={
              <ProtectedRoute requiredPermission="gerar-cobranca">
                <GerarCobranca />
              </ProtectedRoute>
            }
          />
          
          {/* Relatórios */}
          <Route
            path="relatorios"
            element={
              <ProtectedRoute requiredPermission="relatorios">
                <Relatorios />
              </ProtectedRoute>
            }
          />
          
          {/* Marketing */}
          <Route
            path="marketing"
            element={
              <ProtectedRoute requiredPermission="marketing">
                <Marketing />
              </ProtectedRoute>
            }
          />
          
          {/* Envio */}
          <Route
            path="envio"
            element={
              <ProtectedRoute requiredPermission="envio">
                <Envio />
              </ProtectedRoute>
            }
          />
          
          {/* Envios ClickSign */}
          <Route path="clicksign">
            <Route index element={<Navigate to="/clicksign/contrato-adesao" replace />} />

            <Route
              path="contrato-adesao"
              element={
                <ProtectedRoute requiredPermission={['clicksign', 'clicksign:contrato']}>
                  <ClicksignEnvios tipoDocumento="contrato_adesao" />
                </ProtectedRoute>
              }
            />

            <Route
              path="termo-cancelamento"
              element={
                <ProtectedRoute requiredPermission={['clicksign', 'clicksign:termo']}>
                  <ClicksignEnvios tipoDocumento="termo_cancelamento" />
                </ProtectedRoute>
              }
            />

            <Route
              path="acompanhar"
              element={
                <ProtectedRoute requiredPermission={['clicksign', 'clicksign:acompanhar']}>
                  <ClicksignAcompanhar />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Esteira de Vendas */}
          <Route
            path="esteira-vendas"
            element={
              <ProtectedRoute requiredPermission="esteira-vendas">
                <EsteiraVendas />
              </ProtectedRoute>
            }
          />
          
          {/* Checklist */}
          <Route path="checklist">
            <Route index element={<Navigate to="/checklist/medico" replace />} />
            <Route
              path="medico"
              element={
                <ProtectedRoute requiredPermission={['checklist', 'checklist:medico']}>
                  <Checklist tipo="medico" />
                </ProtectedRoute>
              }
            />
            <Route
              path="odontologia"
              element={
                <ProtectedRoute requiredPermission={['checklist', 'checklist:odontologia']}>
                  <Checklist tipo="odontologia" />
                </ProtectedRoute>
              }
            />
            <Route
              path="cartao"
              element={
                <ProtectedRoute requiredPermission={['checklist', 'checklist:cartao']}>
                  <Checklist tipo="cartao" />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Agendamentos */}
          <Route path="agendamentos">
            <Route index element={<Navigate to="/agendamentos/odontologia" replace />} />
            <Route path="todos" element={<Navigate to="/agendamentos/odontologia" replace />} />
            <Route
              path="medico"
              element={
                <ProtectedRoute requiredPermission={['agendamentos', 'agendamentos:medico']}>
                  <AgendamentoMedico />
                </ProtectedRoute>
              }
            />
            <Route
              path="odontologia"
              element={
                <ProtectedRoute requiredPermission={['agendamentos', 'agendamentos:odontologia-agenda']}>
                  <AgendamentoOdontologia />
                </ProtectedRoute>
              }
            />
            <Route
              path="medico/conferencia-valores"
              element={
                <ProtectedRoute requiredPermission="agendamentos:conferencia-valores-exames">
                  <ConferenciaValoresExames />
                </ProtectedRoute>
              }
            />
            <Route
              path="medico/consultorio"
              element={
                <ProtectedRoute requiredPermission={['agendamentos', 'agendamentos:medico']}>
                  <AgendamentosConsultorio setor="medico" />
                </ProtectedRoute>
              }
            />
            <Route
              path="odontologia/consultorio"
              element={
                <ProtectedRoute requiredPermission={['agendamentos', 'agendamentos:odontologia-agenda']}>
                  <AgendamentosConsultorio setor="odontologia" />
                </ProtectedRoute>
              }
            />
            <Route
              path="carteira-odonto"
              element={
                <ProtectedRoute requiredPermission={['agendamentos', 'agendamentos:odontologia']}>
                  <AgendamentoCarteiraOdonto />
                </ProtectedRoute>
              }
            />
          </Route>
          
          {/* Meu Perfil (todo usuario logado) */}
          <Route
            path="meu-perfil"
            element={
              <ProtectedRoute>
                <MeuPerfil />
              </ProtectedRoute>
            }
          />

          {/* Atendimento */}
          <Route
            path="atendimento"
            element={
              <ProtectedRoute requiredPermission="atendimento">
                <CustomerService />
              </ProtectedRoute>
            }
          />
          
          {/* Administração */}
          <Route
            path="administracao"
            element={
              <ProtectedRoute requiredPermission="administracao">
                <Administracao />
              </ProtectedRoute>
            }
          />
          
          {/* Relatórios Marketing */}
          <Route
            path="relatorios-marketing"
            element={
              <ProtectedRoute requiredPermission="marketing">
                <MarketingReports />
              </ProtectedRoute>
            }
          />
          
          {/* Contatos Marketing */}
          <Route
            path="marketing-contacts"
            element={
              <ProtectedRoute requiredPermission="marketing">
                <MarketingContacts />
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;